import { Component, Inject, Input, OnDestroy } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import {
  ActivityBodyInterface,
  ActivityInterface,
  LocationInterface,
} from '@sport-activities/sdk';
import { Subscription } from 'rxjs';

import { InputStyleEnum } from '~/component/form/input/enums/style.enum';
import { FormGroupValueInterface } from '~/component/form/interfaces/form-group-value.interface';
import { RoutingInterface } from '~/core/services/navigation/interfaces/routing.interface';
import { NavigationService } from '~/core/services/navigation/navigation.service';
import { TitleService } from '~/core/services/title/title.service';
import { AbstractOrganizerActivityCreationFormPageDirective } from '~/organizer/activity-creation/components/shared/form-wrapper/classes/abstract-activity-creation-form-page';
import { NAVIGATION_ROUTING } from '~/organizer/shared/components/shared/navigation-buttons/navigation-buttons.component';
import { OrganizerSharedActivityService } from '~/organizer/shared/services/activity/activity.service';
import { OrganizerSharedLocatableService } from '~/organizer/shared/services/locatable/locatable.service';
import { OrganizerSharedOrganizationService } from '~/organizer/shared/services/organization/organization.service';

@Component({
  selector: 'app-organizer-activity-creation-address',
  templateUrl: './address.component.html',
  styleUrls: [
    '../../../../../organizer/shared/components/shared/form-wrapper/form-wrapper.component.scss',
  ],
})
export class OrganizerActivityCreationPageAddressComponent
  extends AbstractOrganizerActivityCreationFormPageDirective
  implements OnDestroy
{
  @Input() public inputStyle = InputStyleEnum.Shadowed;
  private readonly subscriptions = new Subscription();
  protected readonly pageTitleKey =
    'seo.organizer.activity_creation.address.title';

  public address: LocationInterface;
  public placeErrorMessages: string[] = [];

  constructor(
    private readonly translateService: TranslateService,
    private readonly locatableService: OrganizerSharedLocatableService,
    protected readonly activatedRoute: ActivatedRoute,
    protected readonly activityService: OrganizerSharedActivityService,
    protected readonly formBuilder: FormBuilder,
    protected readonly navigationService: NavigationService,
    protected readonly organizationService: OrganizerSharedOrganizationService,
    @Inject(NAVIGATION_ROUTING)
    protected readonly navigationRouting: RoutingInterface[],
    protected readonly titleService: TitleService
  ) {
    super(
      activatedRoute,
      activityService,
      formBuilder,
      navigationService,
      organizationService,
      navigationRouting,
      titleService
    );
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  protected buildActivityData(
    formGroupValue: FormGroupValueInterface
  ): Partial<ActivityInterface> {
    const data = super.buildActivityData(
      formGroupValue
    ) as Partial<ActivityInterface>;
    data.location.precision = formGroupValue.description || null;

    return data;
  }

  protected buildDataBeforeSubmit(
    formGroupValue: FormGroupValueInterface
  ): Partial<ActivityBodyInterface> {
    if (!formGroupValue?.address?.address?.postalCode) {
      const postalCode =
        formGroupValue?.address?.address?.streetAddress.split(',')[1];

      formGroupValue.address.address.postalCode = postalCode;
    }

    return {
      location: {
        ...formGroupValue.address,
        precision: [
          this.locatableService.buildCurrentLocatableBody(
            formGroupValue.address.precision || null
          ),
        ],
      },
    };
  }

  protected buildValidationConfig(): void {
    const activity = this.activityService.activity;

    this.validationConfig = {
      address: [
        activity.location?.identifier ? activity.location : null,
        Validators.required,
      ],
    };
  }

  protected onInitHook(): void {
    this.address = this.activityService.activity.location;

    this.subscriptions.add(
      this.formGroup.get('address').valueChanges.subscribe(() => {
        this.placeErrorMessages = [];
      })
    );
  }

  public clickNextNavigationButton(): void {
    if (!this.formGroup.valid) {
      this.placeErrorMessages = [
        this.translateService.instant(
          'activity_creation.address.required.error'
        ),
      ];
    }
  }

  public submit(formGroupValue: FormGroupValueInterface): void {
    if (!this.formGroup.valid) {
      this.placeErrorMessages = [
        this.translateService.instant(
          'activity_creation.address.required.error'
        ),
      ];
      return;
    }

    super.submit(formGroupValue);
  }
}
